import { useDocument } from "@/composables/useFirestore";
import { db } from "@/firebase";
import type { Transaction } from "@/utils/types";
import type { Account } from "@aircap/common/account";
import { collection, doc } from "firebase/firestore";
import { computed, type Ref } from "vue";
import { useBridgeTransactions } from "./useBridgeTransactions.js";
import { useGclTransactions } from "./useGclTransactions.js";
import { usePowensTransactions } from "./usePowensTransactions.js";
import { useSwanTransactions } from "./useSwanTransactions.js";
import { useTreezorTransactions } from "./useTreezorTransactions.js";

export const useTransactions = (
  accountId: Ref<string>,
  filters?: {
    beneficiaryIban?: Ref<string | undefined>;
    isFuture?: Ref<boolean>;
  },
) => {
  const accountSource = computed(() => doc(collection<Account>(db, "accounts"), accountId.value));
  const { data: account } = useDocument<Account>(accountSource);

  // Keep the account ref to undefined when it's not the right account to prevent loading firestore data (even if there is zero data to load).
  const accountIfBridge = computed(() =>
    account.value?.accountType === "bridge" ? account.value : undefined,
  );
  const accountIfPowens = computed(() =>
    account.value?.accountType === "powens" ? account.value : undefined,
  );
  const accountIfGcl = computed(() =>
    account.value?.accountType === "gcl" ? account.value : undefined,
  );
  const accountIfSwan = computed(() =>
    account.value?.accountType === "swan" ? account.value : undefined,
  );
  const accountIfTreezor = computed(() =>
    account.value?.accountType === "treezor" ? account.value : undefined,
  );

  const {
    loading: loadingBridge,
    transactions: bridgeTransactions,
    flagTransaction: flagBridgeTransaction,
  } = useBridgeTransactions(accountIfBridge, filters);

  const {
    loading: loadingSwan,
    transactions: swanTransactions,
    flagTransaction: flagSwanTransaction,
  } = useSwanTransactions(accountIfSwan, filters);

  const {
    loading: loadingPowens,
    transactions: powensTransactions,
    flagTransaction: flagPowensTransaction,
  } = usePowensTransactions(accountIfPowens, filters);

  const {
    loading: loadingGcl,
    transactions: gclTransactions,
    flagTransaction: flagGclTransaction,
  } = useGclTransactions(accountIfGcl, filters);

  const {
    loading: loadingTreezor,
    transactions: treezorTransactions,
    flagTransaction: flagTreezorTransaction,
  } = useTreezorTransactions(accountIfTreezor, filters);

  const transactions = computed<Transaction[]>(() => {
    switch (account.value?.accountType) {
      case undefined:
        return [];
      case "bridge": {
        return bridgeTransactions.value;
      }
      case "swan": {
        return swanTransactions.value;
      }
      case "powens": {
        return powensTransactions.value;
      }
      case "gcl": {
        return gclTransactions.value;
      }
      case "treezor": {
        return treezorTransactions.value;
      }
    }
  });

  const loading = computed<boolean>(() => {
    switch (account.value?.accountType) {
      case undefined: {
        return true;
      }
      case "bridge": {
        return loadingBridge.value;
      }
      case "swan": {
        return loadingSwan.value;
      }
      case "powens": {
        return loadingPowens.value;
      }
      case "gcl": {
        return loadingGcl.value;
      }
      case "treezor": {
        return loadingTreezor.value;
      }
    }
  });

  const flagTransaction = (transactionId: string, value: boolean) => {
    switch (account.value?.accountType) {
      case undefined: {
        break;
      }
      case "bridge": {
        return flagBridgeTransaction(transactionId, value);
      }
      case "swan": {
        return flagSwanTransaction(transactionId, value);
      }
      case "powens": {
        return flagPowensTransaction(transactionId, value);
      }
      case "gcl": {
        return flagGclTransaction(transactionId, value);
      }
      case "treezor": {
        return flagTreezorTransaction(transactionId, value);
      }
    }
  };

  return {
    transactions,
    loading,
    flagTransaction,
  };
};
